import { FC, Fragment, useState } from 'react'
import { useRouter } from 'next/router'

import Button from './base/forms/Button'
import Checkbox from './base/forms/Checkbox'
import GroupSelect from './project/GroupSelect'
import KYCStatus from './project/KYCStatus'
import ModalDefault from './base/ModalDefault'
import { ErrorMessage } from './base/Messages'
import { toast } from './base/Toast'
import { SubscriptionSummary } from 'common/types/responses'
import { useCreateAssignCompanyGroupsMutation } from 'common/services/useAssignCompanyGroup'
import { useGetGroupsQuery } from 'common/services/useGroup'

type ReassignCustomerType = {
  from?: number
  isOpen: boolean
  onComplete?: () => void
  selectedCustomers: SubscriptionSummary[]
  setIsOpen: (value: boolean) => void
}

const ReassignCustomer: FC<ReassignCustomerType> = ({
  from,
  isOpen,
  onComplete,
  selectedCustomers,
  setIsOpen,
}) => {
  const [step, setStep] = useState(1)
  const router = useRouter()
  const [reassignTo, setReassignTo] = useState<number>()
  const { data: groups } = useGetGroupsQuery({})
  const [createAssignment, { error, isLoading }] =
    useCreateAssignCompanyGroupsMutation({})

  const groupId = Number(router.query.id)
  const handleClose = () => setIsOpen(false)

  const handleAssign = () => {
    if (!reassignTo) return
    createAssignment({
      subscriptions: selectedCustomers,
      to: reassignTo,
    }).then((res) => {
      if (!('error' in res)) {
        handleClose()
        onComplete?.()
        toast('Success', 'Reassigned companies')
      }
    })
  }

  return (
    <ModalDefault
      title='Reassign Customers'
      preventDismiss={isLoading}
      isOpen={isOpen}
      onDismiss={handleClose}
      toggle={handleClose}
      size='lg'
    >
      <ErrorMessage>{error}</ErrorMessage>
      {step === 2 ? (
        <>
          <h3 className='fw-semibold mb-6'>Step 2: Confirm Group Assignment</h3>
          {!!from && (
            <GroupSelect
              label='Current Assigned Group'
              value={groupId}
              disabled
              onChange={() => {}}
              className='mb-5'
            />
          )}

          <GroupSelect
            label='Assign to Group'
            value={reassignTo ? [reassignTo] : []}
            excludeGroups={[groupId]}
            onChange={(v) => setReassignTo(v.value)}
            defaultValue={router.query.id as any}
          />
          <div className='bg-light300 rounded p-4 text-muted fs-xs mt-24'>
            Add teams members using the search field above. Please add any
            relevant team members via the invite functionality on the 'Members'
            tab.
          </div>
          <div className='text-end mt-6'>
            <Button
              style={{ width: 160 }}
              onClick={handleClose}
              className='me-2'
              theme='secondary'
            >
              Cancel
            </Button>
            <Button
              style={{ width: 160 }}
              className='me-2'
              disabled={isLoading || !reassignTo}
              onClick={handleAssign}
            >
              Confirm & Assign
            </Button>
          </div>
        </>
      ) : (
        <>
          <h3 className='fw-semibold mb-0'>
            Step 1: Confirm Customers to reassign
          </h3>
          <div
            style={{ maxHeight: 300 }}
            className='d-flex overflow-y-auto flex-column mt-8 pe-2'
          >
            {selectedCustomers.map((customer) => {
              const customerGroup = customer.groups?.[0]
              const group =
                !!customerGroup &&
                groups?.results?.find((v) => v.id === customerGroup?.group_id)
              return (
                <Fragment key={customer.id}>
                  <div className='d-flex flex-row justify-content-between'>
                    <div className='d-flex gap-4'>
                      <Checkbox id={customer.id.toString()} checked={true} />
                      <div className='d-flex flex-column'>
                        <span className='fw-semibold'>
                          {customer.company_name}
                        </span>
                        <span>{customer.email_address}</span>
                      </div>
                    </div>
                    <KYCStatus subscription={customer} />
                  </div>
                  {!from && !!group && (
                    <span className='fs-xs mt-2'>
                      This customer will be removed from group{' '}
                      <strong>{group.name}</strong>
                    </span>
                  )}
                  {!from && !group && (
                    <span className='fs-xs text-muted mt-2'>
                      This customer is not currently a member of a group
                    </span>
                  )}
                  <hr className='my-3' />
                </Fragment>
              )
            })}
          </div>
          <div className='text-end mt-4'>
            <Button
              onClick={handleClose}
              style={{ width: 125 }}
              disabled={isLoading}
              className='me-2'
              theme='secondary'
            >
              Cancel
            </Button>
            <Button
              style={{ width: 125 }}
              disabled={isLoading}
              className='me-2'
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </ModalDefault>
  )
}

export default ReassignCustomer
